<script setup lang="ts">
import { onUnmounted, ref } from 'vue';
import { onKeyStroke } from '@vueuse/core';

import { AppHighlighter, KeyboardKeyCode, AppSvgIcon } from '@libs/ui';
import {
  isAirportSuggestion,
  isFerryRouteSuggestion,
} from '@libs/trip-planner-interfaces';
import type {
  AutoCompleteSuggestion,
  FlightAutoCompleteSuggestion,
  FerryAutoCompleteSuggestion,
} from '@libs/trip-planner-interfaces';

import { FerryRouteItem } from '../ferryRouteItem';

import { AutoCompleteType } from './AutoComplete.model';
import MyPositionButton from './MyPositionButton.vue';
import { getIconNameFromAutoCompleteSuggestionCategory } from './AutoComplete.helper';
import { useAutoCompleteStore } from './stores/autoCompleteStore';

interface AutoCompleteSuggestionsListEmits {
  (eventName: 'select-suggestion', suggestion: AutoCompleteSuggestion): void;
  (
    eventName: 'select-airport-suggestion',
    suggestion: FlightAutoCompleteSuggestion
  ): void;
  (eventName: 'select-my-position'): void;
  (eventName: 'select-ferry-route', suggestion: FerryAutoCompleteSuggestion);
}
const emit = defineEmits<AutoCompleteSuggestionsListEmits>();

const autoCompleteStore = useAutoCompleteStore();

const currentSelectedItem = ref<
  { categoryIndex: number; itemIndex: number } | undefined
>(undefined);

function emitSelectedSuggestion(suggestion: AutoCompleteSuggestion): void {
  if (isAirportSuggestion(suggestion)) {
    emit('select-airport-suggestion', suggestion);
  } else if (isFerryRouteSuggestion(suggestion)) {
    emit('select-ferry-route', suggestion);
  } else {
    emit('select-suggestion', suggestion);
  }

  autoCompleteStore.cleanAutoCompleteSuggestions();
  autoCompleteStore.searchText = suggestion.label;
}

function handleKeyDown(event: KeyboardEvent): void {
  // NOTE: To prevent page scroll
  event.preventDefault();

  switch (event.key) {
    case KeyboardKeyCode.ArrowUp:
      if (currentSelectedItem.value === undefined) {
        setLastItemInLastCategoryAsCurrentOne();
      } else {
        moveFocus(KeyboardKeyCode.ArrowUp);
      }
      break;

    case KeyboardKeyCode.ArrowDown:
      if (currentSelectedItem.value === undefined) {
        currentSelectedItem.value = { categoryIndex: 0, itemIndex: 0 };
      } else {
        moveFocus(KeyboardKeyCode.ArrowDown);
      }
      break;

    case KeyboardKeyCode.Enter:
      {
        if (currentSelectedItem.value) {
          const selectedSuggestion = getSuggestion(
            currentSelectedItem.value.categoryIndex,
            currentSelectedItem.value.itemIndex
          );

          if (selectedSuggestion) {
            emitSelectedSuggestion(selectedSuggestion);
          }
        }
      }
      break;

    default:
      break;
  }
}

function moveFocus(
  direction: KeyboardKeyCode.ArrowUp | KeyboardKeyCode.ArrowDown
): void {
  if (!currentSelectedItem.value) {
    return;
  }

  const { categoryIndex, itemIndex } = currentSelectedItem.value;

  if (direction === KeyboardKeyCode.ArrowDown) {
    const nextInCurrentCategory = getSuggestion(categoryIndex, itemIndex + 1);

    if (nextInCurrentCategory) {
      currentSelectedItem.value = {
        categoryIndex: currentSelectedItem.value.categoryIndex,
        itemIndex: currentSelectedItem.value.itemIndex + 1,
      };
      return;
    }

    const firstInNextCategory = getSuggestion(categoryIndex + 1, 0);

    if (firstInNextCategory) {
      currentSelectedItem.value = {
        categoryIndex: currentSelectedItem.value.categoryIndex + 1,
        itemIndex: 0,
      };
      return;
    }

    currentSelectedItem.value = { categoryIndex: 0, itemIndex: 0 };
    return;
  }

  if (direction === KeyboardKeyCode.ArrowUp) {
    const previousInCurrentCategory = getSuggestion(
      categoryIndex,
      itemIndex - 1
    );

    if (previousInCurrentCategory) {
      currentSelectedItem.value = {
        categoryIndex: currentSelectedItem.value.categoryIndex,
        itemIndex: currentSelectedItem.value.itemIndex - 1,
      };
      return;
    }

    const previousCategory =
      autoCompleteStore.autoCompleteSuggestions[categoryIndex - 1]?.data;

    if (previousCategory) {
      const lastInPreviousCategory = getSuggestion(
        categoryIndex - 1,
        previousCategory.length - 1
      );

      if (lastInPreviousCategory) {
        currentSelectedItem.value = {
          categoryIndex: categoryIndex - 1,
          itemIndex: previousCategory.length - 1,
        };
        return;
      }
    }
    setLastItemInLastCategoryAsCurrentOne();
    return;
  }
}

function setLastItemInLastCategoryAsCurrentOne(): void {
  const lastCategoryIndex =
    autoCompleteStore.autoCompleteSuggestions.length - 1;
  const lastCategory =
    autoCompleteStore.autoCompleteSuggestions[lastCategoryIndex];

  if (lastCategory) {
    const lastItemIndex = lastCategory.data.length - 1;

    currentSelectedItem.value = {
      categoryIndex: lastCategoryIndex,
      itemIndex: lastItemIndex,
    };
  }
}

function getSuggestion(
  categoryIndex: number,
  itemIndex: number
): AutoCompleteSuggestion | undefined {
  return autoCompleteStore.autoCompleteSuggestions[categoryIndex]?.data[
    itemIndex
  ];
}

onKeyStroke(
  [KeyboardKeyCode.ArrowDown, KeyboardKeyCode.ArrowUp, KeyboardKeyCode.Enter],
  handleKeyDown
);

onUnmounted(() => {
  autoCompleteStore.resetAutoCompleteStore();
});
</script>

<template>
  <MyPositionButton
    v-if="autoCompleteStore.autoCompleteForPosition"
    class="my-position-button"
    @click="emit('select-my-position')"
  />
  <ul
    v-if="autoCompleteStore.autoCompleteSuggestions"
    class="auto-complete-suggestions-list"
  >
    <li
      v-for="(
        category, categoryIndex
      ) in autoCompleteStore.autoCompleteSuggestions"
      :key="category.label"
      class="category"
    >
      <div v-if="category.data.length" class="category-name">
        {{ category.label }}
      </div>
      <ul>
        <li
          v-for="(item, itemIndex) in category.data"
          :key="item.label"
          class="category-item"
          :class="{
            focused:
              currentSelectedItem?.categoryIndex === categoryIndex &&
              currentSelectedItem?.itemIndex === itemIndex,
          }"
          @click="emitSelectedSuggestion(item)"
        >
          <FerryRouteItem
            v-if="
              isFerryRouteSuggestion(item) &&
              autoCompleteStore.autoCompleteType === AutoCompleteType.FerryRoute
            "
            :auto-complete-text="autoCompleteStore.searchText"
            :item="item"
          />
          <template v-else>
            <AppSvgIcon
              :name="
                getIconNameFromAutoCompleteSuggestionCategory(category.type)
              "
              size="18"
            />
            <span class="category-item-text">
              <AppHighlighter
                :text="item.label"
                :search-text="autoCompleteStore.searchText"
              />
            </span>
          </template>
        </li>
      </ul>
    </li>
  </ul>
</template>

<style lang="scss" scoped>
ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

.my-position-button {
  margin-bottom: var(--spacing-24);
}

.auto-complete-suggestions-list {
  .category {
    &:not(:last-child) {
      margin-bottom: var(--spacing-24);
    }

    .category-name {
      color: var(--color-neutral-70);
      @include font-label-s-bold;
      margin-bottom: var(--spacing-8);
    }

    .category-item {
      display: flex;
      align-items: center;
      gap: var(--spacing-16);

      padding: var(--spacing-12);
      cursor: pointer;
      border-radius: var(--border-radius-12);
      transition: all var(--transition-medium);

      &.focused {
        background-color: var(--color-neutral-10);
      }
      @include on-hover {
        background-color: var(--color-neutral-10);
      }

      .category-item-text {
        color: var(--color-neutral-70);
        @include font-label-m-light;
      }
    }
  }
}
</style>
